<template>
  <div class="v2-profile-general">
    <v2-card>
      <template #header>
        <h3 class="v2-heading-sm">
          General Information
        </h3>
      </template>

      <!-- Logo & Banner Row -->
      <div class="v2-profile-general__media-row">

        <!-- Logo Section -->
        <div class="v2-profile-general__logo-section">
          <div class="v2-profile-general__logo-preview">
            <img
              v-if="logoImg"
              :src="logoImg"
              alt="Logo"
              class="v2-profile-general__logo-img"
            >
            <div
              v-else
              class="v2-profile-general__logo-placeholder"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                />
                <circle
                  cx="9"
                  cy="9"
                  r="2"
                />
                <path d="m21 15-5-5L5 21" />
              </svg>
            </div>
          </div>
          <div class="v2-profile-general__logo-actions">
            <input
              ref="logoFileInputRef"
              type="file"
              accept="image/*"
              class="v2-profile-general__file-input"
              :disabled="isNotEntityAdmin"
              @change="onLogoFileChange"
            >
            <v2-button
              variant="outline"
              size="sm"
              :disabled="isNotEntityAdmin"
              @click="$refs.logoFileInputRef.click()"
            >
              Upload Logo
            </v2-button>
            <v2-button
              v-if="logoImg"
              variant="outline"
              size="sm"
              :disabled="isNotEntityAdmin"
              @click="onResetLogo"
            >
              Remove Logo
            </v2-button>
          </div>
        </div>

        <!-- Banner Section -->
        <div class="v2-profile-general__banner-section">
          <div class="v2-profile-general__banner-header">
            <span class="v2-profile-general__section-label">Banners</span>
            <v2-button
              variant="outline"
              size="sm"
              :disabled="isNotEntityAdmin"
              @click="showBannerModal = true"
            >
              Manage Banners
            </v2-button>
          </div>
          <div
            v-if="bannerImgs && bannerImgs.length"
            class="v2-profile-general__banner-thumbs"
          >
            <img
              v-for="(img, idx) in bannerImgs"
              :key="idx"
              :src="img"
              :alt="`Banner ${idx + 1}`"
              class="v2-profile-general__banner-thumb"
            >
          </div>
          <p
            v-else
            class="v2-profile-general__no-banners"
          >
            No banners uploaded yet.
          </p>
        </div>

      </div><!-- /media-row -->

      <!-- Form Grid -->
      <div class="v2-profile-general__grid">
        <!-- Entity Name -->
        <div class="v2-profile-general__field">
          <v2-input
            v-model="generalLocal.entityName"
            :label="entityName"
            :placeholder="entityName"
            :disabled="isNotEntityAdmin"
          />
        </div>

        <!-- Position (readonly) -->
        <div class="v2-profile-general__field">
          <v2-input
            :value="USER_ROLES_LABEL[generalLocal.role] || generalLocal.role"
            label="Position"
            placeholder="Position"
            disabled
          />
        </div>

        <!-- First Name -->
        <div class="v2-profile-general__field">
          <v2-input
            v-model="generalLocal.firstName"
            label="First Name"
            placeholder="Enter first name"
          />
        </div>

        <!-- Last Name -->
        <div class="v2-profile-general__field">
          <v2-input
            v-model="generalLocal.lastName"
            label="Last Name"
            placeholder="Enter last name"
          />
        </div>

        <!-- Country -->
        <div class="v2-profile-general__field">
          <v2-select
            v-model="generalLocal.country"
            label="Country"
            :options="countryOptions"
            placeholder="Select country"
            :disabled="isNotEntityAdmin"
          />
        </div>

        <!-- Phone -->
        <div class="v2-profile-general__field">
          <v2-phone-input
            :value="fullPhoneNumber"
            label="Phone"
            :disabled="isNotEntityAdmin"
            :error="isNotValidPhoneNumber ? 'Please enter a valid phone number' : ''"
            @phone-input="onInputPhoneNumber"
          />
        </div>

        <!-- Store Type (Retailer only) -->
        <div
          v-if="isRetailer"
          class="v2-profile-general__field"
        >
          <v2-select
            v-model="generalLocal.storeType"
            label="Store Type"
            :options="storeTypeOptions"
            option-label="LABEL"
            :reduce="s => s.LABEL"
            placeholder="Select store type"
            :disabled="isNotEntityAdmin"
          />
        </div>

        <!-- About -->
        <div class="v2-profile-general__field v2-profile-general__field--full">
          <label class="v2-profile-general__label">About</label>
          <textarea
            v-model="generalLocal.storeDescription"
            class="v2-profile-general__textarea"
            placeholder="Describe your company..."
            :disabled="isNotEntityAdmin"
            rows="4"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="v2-profile-general__actions">
        <v2-button
          variant="primary"
          :loading="saving"
          :disabled="saving"
          @click="onSave"
        >
          Save changes
        </v2-button>
        <v2-button
          variant="outline"
          :disabled="saving"
          @click="onReset"
        >
          Reset
        </v2-button>
      </div>
    </v2-card>

    <!-- Logo Crop Modal -->
    <v2-modal
      :show="showCropModal"
      title="Crop Logo"
      size="md"
      @close="showCropModal = false"
    >
      <div
        v-if="croppedImg"
        class="v2-profile-general__cropper-wrap"
      >
        <cropper
          ref="logoCropperRef"
          :src="croppedImg"
          class="v2-profile-general__cropper"
          :stencil-props="{ aspectRatio: 1 }"
        />
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showCropModal = false"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :loading="isCropping"
          @click="onCropLogo"
        >
          Crop & Save
        </v2-button>
      </template>
    </v2-modal>

    <!-- Banner Cropper Modal -->
    <v2-modal
      :show="showBannerModal"
      title="Manage Banners"
      size="lg"
      @close="showBannerModal = false"
    >
      <v2-banner-cropper
        :profile-general="generalLocal"
        @on-save="onBannerSave"
        @on-cancel="showBannerModal = false"
      />
    </v2-modal>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { Cropper } from 'vue-advanced-cropper'
import { useProfileGeneral } from '@/composables/useProfileGeneral'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Select from '@/components/v2/V2Select.vue'
import V2PhoneInput from '@/components/v2/V2PhoneInput.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2BannerCropper from './V2BannerCropper.vue'

export default {
  name: 'V2ProfileGeneral',
  components: {
    V2Card,
    V2Button,
    V2Input,
    V2Select,
    V2PhoneInput,
    V2Modal,
    V2BannerCropper,
    Cropper,
  },
  props: {
    generalData: {
      type: Object,
      default: () => ({}),
    },
    commonData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update-general'],
  setup(props, { emit }) {
    const pg = useProfileGeneral()
    pg.initGeneral(props.generalData)

    const showCropModal = ref(false)
    const showBannerModal = ref(false)
    const logoCropperRef = ref(null)

    const countryOptions = computed(() => props.commonData?.COUNTRIES || [])

    const storeTypeOptions = computed(() => {
      if (!pg.STORE_TYPE) return []
      return Object.values(pg.STORE_TYPE)
    })

    pg.setupLogoWatcher(() => {
      showCropModal.value = true
    })

    function onLogoFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        pg.uncroppedLogoFile.value = file
      }
    }

    async function onCropLogo() {
      const result = await pg.cropLogo(logoCropperRef.value)
      showCropModal.value = false
      pg.uncroppedLogoFile.value = null
      if (result?.success) {
        emit('update-general', pg.generalLocal.value)
      }
    }

    async function onResetLogo() {
      const result = await pg.resetLogo()
      if (result?.success) {
        emit('update-general', pg.generalLocal.value)
      }
    }

    async function onSave() {
      const result = await pg.saveGeneralSettings()
      if (result?.success) {
        emit('update-general', pg.generalLocal.value)
      }
    }

    function onReset() {
      pg.resetForm(props.generalData)
    }

    function onBannerSave() {
      showBannerModal.value = false
      emit('update-general', pg.generalLocal.value)
    }

    watch(
      () => props.generalData,
      newData => {
        if (newData) {
          pg.initGeneral(newData)
        }
      },
    )

    return {
      ...pg,
      showCropModal,
      showBannerModal,
      logoCropperRef,
      countryOptions,
      storeTypeOptions,
      onLogoFileChange,
      onCropLogo,
      onResetLogo,
      onSave,
      onReset,
      onBannerSave,
    }
  },
}
</script>

<style lang="scss">
@import '~vue-advanced-cropper/dist/style.css';

.v2-profile-general__cropper {
  .vue-simple-handler {
    background: var(--v2-primary);
  }

  .vue-preview--fill {
    border: 1px solid var(--v2-primary);
  }
}
</style>

<style lang="scss" scoped>
.v2-profile-general {
  &__media-row {
    display: flex;
    gap: var(--v2-space-5);
    margin-bottom: var(--v2-space-5);
  }

  &__logo-section {
    flex: 1;
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
  }

  // Glass logo placeholder — matches the v2 form-control recipe so the
  // upload area sits in the same visual language as the surrounding
  // inputs (no white square breaking up the form).
  &__logo-preview {
    width: 80px;
    height: 80px;
    border-radius: var(--v2-radius-md);
    border: 1px solid var(--v2-glass-stroke);
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--v2-glass-surface-2);
  }

  &__logo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__logo-placeholder {
    color: var(--v2-muted-foreground);
  }

  &__logo-actions {
    display: flex;
    gap: var(--v2-space-2);
    flex-wrap: wrap;
    align-items: center;
  }

  &__file-input {
    display: none;
  }

  &__banner-section {
    flex: 1;
  }

  &__banner-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--v2-space-2);
  }

  &__section-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__banner-thumbs {
    display: flex;
    gap: var(--v2-space-2);
    flex-wrap: wrap;
  }

  &__banner-thumb {
    width: 120px;
    height: 60px;
    object-fit: cover;
    border-radius: var(--v2-radius-sm);
    border: 1px solid var(--v2-border);
  }

  &__no-banners {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-5);
  }

  &__field {
    display: flex;
    flex-direction: column;

    &--full {
      grid-column: 1 / -1;
    }
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-1);
  }

  // Glass textarea — matches V2Input recipe.
  &__textarea {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    padding: var(--v2-space-2) var(--v2-space-3);
    resize: vertical;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: var(--v2-muted);
    }
  }

  &__actions {
    display: flex;
    gap: var(--v2-space-3);
  }

  @media (max-width: 640px) {
    &__media-row {
      flex-direction: column;
    }

    &__logo-section {
      flex-direction: column;
      align-items: flex-start;
    }

    &__grid {
      grid-template-columns: 1fr;
    }

    &__field--full {
      grid-column: 1;
    }
  }

  &__cropper-wrap {
    max-height: 400px;
    overflow: hidden;
  }

  &__cropper {
    height: 400px;
    background: var(--v2-muted);
  }
}
</style>
